import { useState } from "react";
import { Modal } from "@mui/material";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";
import Input from "components/Input";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import Spinner from "components/Spinner";
import eDeliveryGovService, {
  IGovAddressSearchRequest,
  IGovAddressSearchResult,
  IGovOfficialId,
} from "api/eDeliveryGovService";

// Typ odbiorcy → zbiór kategorii UA (SearchCategory) + widoczne identyfikatory.
type ReceiverType = "person" | "trusted" | "company" | "bailiff";

const CATEGORY_MAP: Record<ReceiverType, string[]> = {
  person: ["INDIVIDUAL"],
  trusted: [
    "ADVOCATE",
    "LEGAL_ADVISOR",
    "TAX_ADVISOR",
    "RESTRUCTURING_ADVISOR",
    "PATENT_ATTORNEY",
    "NOTARY",
    "COUNSELLOR_OF_GCRP",
  ],
  company: ["COMPANY", "ORGANISATION", "PUBLIC_INSTITUTION"],
  bailiff: ["COURT_ENFORCEMENT_OFFICER"],
};

const RECEIVER_TYPES: { key: ReceiverType; label: string }[] = [
  { key: "person", label: "Osoba fizyczna" },
  { key: "trusted", label: "Zawód zaufania publicznego" },
  { key: "company", label: "Podmiot / firma" },
  { key: "bailiff", label: "Komornik" },
];

// Wyszukiwarka adresu ADE w BAE (rządowe e-Doręczenia). Zwraca wybrany rekord do wołającego.
const SearchBaeModal: FC<Props> = ({ toggle, onSelect }) => {
  const [receiverType, setReceiverType] = useState<ReceiverType>("person");
  const [form, setForm] = useState({
    name: "",
    surname: "",
    entityName: "",
    pesel: "",
    nip: "",
    regon: "",
    krs: "",
    city: "",
    postalCode: "",
    street: "",
    buildingNumber: "",
    flatNumber: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<IGovAddressSearchResult[] | null>(null);

  const { mutate: search, isLoading } =
    eDeliveryGovService.useSearchAddressBook();

  const set = (k: keyof typeof form) => (v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  const showPerson = receiverType === "person" || receiverType === "trusted";
  const showCompany = receiverType === "company";
  const showBailiff = receiverType === "bailiff";

  const handleSearch = () => {
    const officialIds: IGovOfficialId[] = [];
    if (form.pesel) officialIds.push({ ReferenceRegistry: "pesel", Id: form.pesel });
    if (form.nip) officialIds.push({ ReferenceRegistry: "nip", Id: form.nip });
    if (form.regon) officialIds.push({ ReferenceRegistry: "regon", Id: form.regon });
    if (form.krs) officialIds.push({ ReferenceRegistry: "krs", Id: form.krs });

    const payload: IGovAddressSearchRequest = {
      Categories: CATEGORY_MAP[receiverType],
      Name: showPerson || showBailiff ? form.name || undefined : undefined,
      Surname: showPerson || showBailiff ? form.surname || undefined : undefined,
      EntityName: showCompany ? form.entityName || undefined : undefined,
      OfficialIds: officialIds.length ? officialIds : undefined,
      City: form.city || undefined,
      PostalCode: form.postalCode || undefined,
      Street: form.street || undefined,
      BuildingNumber: form.buildingNumber || undefined,
      FlatNumber: form.flatNumber || undefined,
    };

    setError(null);
    setResults(null);
    search(payload, {
      onSuccess: (resp) => {
        setResults(resp.data?.Results ?? []);
      },
      onError: () => {
        setError("Wyszukiwanie nie powiodło się. Spróbuj zawęzić kryteria.");
      },
    });
  };

  const displayName = (r: IGovAddressSearchResult) =>
    r.EntityName ||
    [r.Name, r.Surname].filter(Boolean).join(" ") ||
    r.EDeliveryAddress ||
    "—";

  return (
    <Modal open onClose={toggle}>
      <div className={styles.wrapper}>
        <div className={styles.title}>Wyszukiwanie adresu ADE (BAE)</div>

        <div className={styles.types}>
          {RECEIVER_TYPES.map((rt) =>
            rt.key === receiverType ? (
              <Button
                key={rt.key}
                text={rt.label}
                onClick={() => setReceiverType(rt.key)}
              />
            ) : (
              <HollowButton
                key={rt.key}
                text={rt.label}
                onClick={() => {
                  setReceiverType(rt.key);
                  setResults(null);
                }}
              />
            ),
          )}
        </div>

        <div className={styles.form}>
          {showCompany && (
            <Input
              value={form.entityName}
              onChange={set("entityName")}
              label="Nazwa podmiotu / firmy"
            />
          )}
          {(showPerson || showBailiff) && (
            <div className={styles.row}>
              <Input value={form.name} onChange={set("name")} label="Imię" />
              <Input
                value={form.surname}
                onChange={set("surname")}
                label="Nazwisko"
              />
            </div>
          )}

          <div className={styles.row}>
            {showPerson && (
              <Input value={form.pesel} onChange={set("pesel")} label="PESEL" />
            )}
            {(showCompany || showBailiff) && (
              <>
                <Input value={form.nip} onChange={set("nip")} label="NIP" />
                <Input value={form.regon} onChange={set("regon")} label="REGON" />
              </>
            )}
            {showCompany && (
              <Input value={form.krs} onChange={set("krs")} label="KRS" />
            )}
          </div>

          <div className={styles.sectionLabel}>Dane adresowe</div>
          <div className={styles.row}>
            <Input value={form.city} onChange={set("city")} label="Miejscowość" />
            <Input
              value={form.postalCode}
              onChange={set("postalCode")}
              label="Kod pocztowy"
            />
          </div>
          <div className={styles.row}>
            <Input value={form.street} onChange={set("street")} label="Ulica" />
            <Input
              value={form.buildingNumber}
              onChange={set("buildingNumber")}
              label="Nr domu"
            />
            <Input
              value={form.flatNumber}
              onChange={set("flatNumber")}
              label="Nr lokalu"
            />
          </div>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.buttons}>
          <HollowButton text="Zamknij" onClick={toggle} />
          <Button
            text="Szukaj"
            onClick={handleSearch}
            loading={isLoading}
            disabled={isLoading}
          />
        </div>

        {isLoading && <Spinner />}

        {results != null && !isLoading && (
          <div className={styles.results}>
            {results.length === 0 && (
              <div className={styles.empty}>Nie znaleziono adresów.</div>
            )}
            {results.map((r, i) => (
              <div className={styles.result} key={`${r.EDeliveryAddress}-${i}`}>
                <div className={styles.resultInfo}>
                  <div className={styles.resultName}>
                    {displayName(r)}
                    {r.ProfessionalTitle ? ` — ${r.ProfessionalTitle}` : ""}
                  </div>
                  <div className={styles.resultAde}>
                    Adres do e-Doręczeń: {r.EDeliveryAddress}
                  </div>
                  {r.Address && (
                    <div className={styles.resultAddress}>{r.Address}</div>
                  )}
                </div>
                <Button
                  text="Wybierz"
                  onClick={() => {
                    onSelect(r);
                    toggle();
                  }}
                  disabled={!r.EDeliveryAddress}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SearchBaeModal;
